import { useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  FileText,
  Image as ImageIcon,
  Loader2,
  ShieldCheck,
  UploadCloud,
  UserRound,
  XCircle,
} from 'lucide-react';
import { uploadIDDocument, validateExtractedID } from '../services/idVerificationService';

const ID_OPTIONS = [
  { label: 'Aadhaar', value: 'aadhaar' },
  { label: 'PAN', value: 'pan' },
  { label: 'Passport', value: 'passport' },
  { label: 'Driving License', value: 'driving_license' },
];

const stepTitles = {
  upload: 'Upload your ID',
  verify: 'Verify extracted details',
  confirm: 'Confirm your information',
  success: 'Verification submitted',
};

export default function IDVerificationPage({ userId, onComplete }) {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload');
  const [selectedType, setSelectedType] = useState('aadhaar');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    idNumber: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const resetError = () => setError('');

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => prev.filter((item) => !item.includes(field === 'name' ? 'name' : 'ID number')));
  };

  const handleFileSelect = (pickedFile) => {
    if (!pickedFile) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(pickedFile.type) && !pickedFile.name?.match(/\.(jpg|jpeg|png|pdf)$/i)) {
      setError('Please upload a JPG, PNG, or PDF ID document.');
      return;
    }

    resetError();
    setFile(pickedFile);
    if (pickedFile.type.startsWith('image/')) {
      setFilePreview(URL.createObjectURL(pickedFile));
    } else {
      setFilePreview('');
    }
    setFormData({ name: '', idNumber: '' });
    setStep('verify');
  };

  const handleVerifyContinue = () => {
    const fieldErrors = [];

    if (!formData.name.trim()) {
      fieldErrors.push('Holder name is required.');
    }

    if (!formData.idNumber.trim()) {
      fieldErrors.push('ID number is required.');
    }

    const validated = validateExtractedID({
      idType: selectedType,
      idNumber: formData.idNumber,
      name: formData.name,
    });

    if (!validated.valid) {
      fieldErrors.push(...validated.errors);
    }

    if (fieldErrors.length > 0) {
      setValidationErrors(fieldErrors);
      return;
    }

    setValidationErrors([]);
    setStep('confirm');
  };

  const handleSubmit = async () => {
    if (!file || !userId) {
      setError('User information is missing. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await uploadIDDocument(
        userId,
        file,
        {
          idType: selectedType,
          idNumber: formData.idNumber,
          name: formData.name,
        }
      );

      if (!result.success) {
        setError(result.message || 'Unable to submit ID document.');
        setIsSubmitting(false);
        return;
      }

      setSubmitSuccess(true);
      setStep('success');
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err?.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepIndex = ['upload', 'verify', 'confirm', 'success'].indexOf(step);

  return (
    <div className="min-h-screen bg-dark-900 text-white px-4 py-8">
      <div className="mx-auto max-w-4xl rounded-3xl border border-dark-500 bg-dark-800 shadow-2xl shadow-black/30">
        <div className="border-b border-dark-500 px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-amber-400">Identity verification</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">{stepTitles[step] || stepTitles.upload}</h2>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-dark-400 bg-dark-700 px-3 py-2 text-sm text-slate-300">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Secure Upload
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            {['upload', 'verify', 'confirm', 'success'].map((label, index) => (
              <div key={label} className="flex items-center gap-3 flex-1">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold ${
                    index <= currentStepIndex
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : 'border-dark-400 bg-dark-700 text-slate-400'
                  }`}
                >
                  {index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`h-1 flex-1 rounded-full ${
                      index < currentStepIndex ? 'bg-amber-500' : 'bg-dark-500'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8">
          {error && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Select ID type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full rounded-xl border border-dark-400 bg-dark-700 px-4 py-3 text-white outline-none transition focus:border-amber-500"
                  >
                    {ID_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const droppedFile = e.dataTransfer.files?.[0];
                  if (droppedFile) handleFileSelect(droppedFile);
                }}
                className={`relative overflow-hidden rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
                  isDragging
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-dark-400 bg-dark-700/60 hover:border-amber-400/60'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />

                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-dark-600 text-amber-400">
                  <UploadCloud className="h-8 w-8" />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-white">Upload your {ID_OPTIONS.find((i) => i.value === selectedType)?.label || 'ID'} document</h3>
                <p className="mt-2 text-sm text-slate-400">Drag and drop your ID here or browse from your device.</p>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-5 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-dark-900 transition hover:bg-amber-400"
                >
                  Choose file
                </button>
              </div>

              {file && (
                <div className="rounded-2xl border border-dark-500 bg-dark-700 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-dark-600 p-2 text-amber-400">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{file.name}</p>
                        <p className="text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="rounded-lg border border-dark-400 bg-dark-600 px-3 py-2 text-xs text-slate-300 hover:border-red-500/50 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>

                  {filePreview && (
                    <div className="mt-4 overflow-hidden rounded-xl border border-dark-400 bg-dark-800">
                      <img src={filePreview} alt="ID preview" className="max-h-72 w-full object-contain" />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => file && setStep('verify')}
                  disabled={!file}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-semibold text-dark-900 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-dark-500 disabled:text-slate-400"
                >
                  <ImageIcon className="h-4 w-4" />
                  Enter ID details
                </button>
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-dark-500 bg-dark-700 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-200">
                  <FileText className="h-4 w-4 text-amber-400" />
                  Enter document details
                </div>
                <p className="text-sm text-slate-400">Enter the details exactly as they appear on the uploaded document.</p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Holder Name</label>
                  <div className="relative">
                    <UserRound className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="w-full rounded-xl border border-dark-400 bg-dark-700 py-3 pl-10 pr-3 text-white outline-none transition focus:border-amber-500"
                      placeholder="Enter full name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">ID Number</label>
                  <div className="relative">
                    <CreditCard className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
                    <input
                      type="text"
                      value={formData.idNumber}
                      onChange={(e) => updateField('idNumber', e.target.value)}
                      className="w-full rounded-xl border border-dark-400 bg-dark-700 py-3 pl-10 pr-3 text-white outline-none transition focus:border-amber-500"
                      placeholder="Enter ID number"
                    />
                  </div>
                </div>
              </div>

              {(validationErrors.length > 0 || error) && (
                <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-red-200">
                    <XCircle className="h-4 w-4" />
                    Validation issues
                  </div>
                  <ul className="space-y-1 text-sm text-red-200">
                    {validationErrors.map((item, index) => (
                      <li key={`${item}-${index}`}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="inline-flex items-center gap-2 rounded-xl border border-dark-400 bg-dark-700 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:border-dark-300 hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>

                <button
                  type="button"
                  onClick={handleVerifyContinue}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-dark-900 transition hover:bg-amber-400"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-dark-500 bg-dark-700 p-5">
                <h3 className="mb-4 text-lg font-semibold text-white">Review and confirm</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-dark-500 pb-3">
                    <span className="text-sm text-slate-400">Document Type</span>
                    <span className="text-sm font-medium text-white">
                      {ID_OPTIONS.find((option) => option.value === selectedType)?.label || selectedType}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-dark-500 pb-3">
                    <span className="text-sm text-slate-400">Holder Name</span>
                    <span className="text-sm font-medium text-white">{formData.name}</span>
                  </div>

                  <div className="flex items-center justify-between border-b border-dark-500 pb-3">
                    <span className="text-sm text-slate-400">ID Number</span>
                    <span className="text-sm font-medium text-white">{formData.idNumber}</span>
                  </div>

                </div>
              </div>

              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                Please confirm that the details above are accurate before submitting for verification.
              </div>

              <div className="flex justify-between gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('verify')}
                  className="inline-flex items-center gap-2 rounded-xl border border-dark-400 bg-dark-700 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:border-dark-300 hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-dark-500"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                <CheckCircle2 className="h-10 w-10" />
              </div>

              <div>
                <h3 className="text-2xl font-semibold text-white">Verification submitted successfully</h3>
                <p className="mt-2 text-slate-300">
                  Your ID document is being reviewed. You will be notified once the verification status changes.
                </p>
              </div>

              {submitSuccess && (
                <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                  Your information has been stored successfully and the process has been completed.
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  console.log('🔘 Continue button clicked, calling onComplete...');
                  if (onComplete) {
                    onComplete();
                  } else {
                    console.warn('⚠️ onComplete callback is not provided');
                  }
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-semibold text-dark-900 transition hover:bg-amber-400"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
